import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { WarningIcon } from '@clubspark-react/ui';
import CloseIcon from '@mui/icons-material/Close';
import { FormControlLabel, Grid, IconButton, Typography } from '@mui/material';
import {
  type Column,
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import HelpIcon from 'assets/icons/icn _ 24px _ help.svg?react';
import { eventsAtom } from 'atoms/events';
import { tournamentIdAtom } from 'atoms/tournament';
import TMXShowOnceAlert from 'components/alert/showOnce/TMXShowOnceAlert';
import FilteringNoResults from 'components/emptyStates/FilteringNoResults';
import { RatingTypeEnum } from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/types';
import { TMXSwitch } from 'components/switch/TMXSwitch';
import EnhancedTable from 'components/tables/enhancedTable/EnhancedTable';
import { getGenderMap, getGendersPresent, playerColumnIds, useTableColumns } from 'components/tables/players/utils';
import { useStyles } from 'components/tables/styles';
import { useStylesTooltip } from 'components/tooltip/styles';
import TMXTooltip from 'components/tooltip/TMXTooltip';
import { BodyLargeBold } from 'components/typography/Typography';
import { emptyObject } from 'components/utils';
import { Feature, featureEnabled } from 'config/clients';
import { NO_SELECTION } from 'constants/generic';
import { usePlayersProfileDialog } from 'containers/dialogs/PlayersProfile';
import { usePlayerPopover } from 'containers/players/PlayerActions';
import RankingsAndRatingsContainer from 'containers/rankingsAndRatings/RankingsAndRatingsContainer';
import useMutateTournament from 'hooks/tournament/useMutateTournament';
import useTournamentParticipants from 'hooks/tournament/useTournamentParticipants';
import useVirtualCheckIn, { VirtualCheckInType } from 'hooks/useVirtualCheckIn';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';
import {
  getPlayersFromParticipants,
  getTeamsFromParticipants,
  isParticipantRequestedWithdrawal,
  isParticipantSuspended,
} from 'utils/participant';

import PlayerBulkActions from './PlayerBulkActions';
import PlayersFilters from './PlayersFilters';

export enum SignedInStatusEnum {
  SIGNED_IN = 'SIGNED_IN',
  SIGNED_OUT = 'SIGNED_OUT',
}

const getRowId = (row) => row.participantId;

interface PlayersTableProps {
  savedColumns?: Array<string>;
  savedFilters?: ColumnFiltersState;
  updateSettings?: (data: Record<string, any>) => Promise<void>;
}

const PlayersTable: React.FC<React.PropsWithChildren<PlayersTableProps>> = ({
  savedColumns,
  savedFilters,
  updateSettings,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { classes: tooltipClasses } = useStylesTooltip();

  const { tournamentParticipants } = useTournamentParticipants();
  const tournamentId = useAtomValue(tournamentIdAtom);
  const events = useAtomValue(eventsAtom);
  const openPopover = usePlayerPopover();
  const showProfileDialog = usePlayersProfileDialog();
  const { virtualTournamentCheckIn, updateVirtualCheckIn } = useVirtualCheckIn() ?? {};
  const [signIn] = useMutateTournament('signIn');
  const [removeFromTeam] = useMutateTournament('removePlayerFromTeam');

  const [allowTournamentSignInTooltipOpen, setAllowTournamentSignInTooltipOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    playerColumnIds.reduce((o, key) => Object.assign(o, { [`${key}`]: savedColumns?.includes(key) }), {}),
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([]);

  const filtersRef = useRef<ColumnFiltersState>(null);
  const columnsRef = useRef<Array<string>>(null);
  const selectedEventRef = useRef<string>(NO_SELECTION);

  const teams = useMemo(() => getTeamsFromParticipants(tournamentParticipants), [tournamentParticipants]);
  const players = useMemo(() => getPlayersFromParticipants(tournamentParticipants), [tournamentParticipants]);

  const resetRowSelection = useCallback(() => setRowSelection(emptyObject), []);

  const handleOpenMoreOptions = useCallback(
    (event, rowItem) => {
      const participant = players.find((participant) => participant.participantId === rowItem.participantId);
      openPopover(participant, event.currentTarget);
    },
    [openPopover, players],
  );

  const handleOnAllowTournamentSignInTooltipOpen = useCallback(() => {
    setAllowTournamentSignInTooltipOpen(true);
  }, []);

  const handleOnAllowTournamentSignInTooltipClose = useCallback(() => {
    setAllowTournamentSignInTooltipOpen(false);
  }, []);

  const handleOnToggleTournamentSignIn = useCallback(() => {
    updateVirtualCheckIn(VirtualCheckInType.TOURNAMENT);
  }, [updateVirtualCheckIn]);

  const columns = useTableColumns({
    teams,
    classes,
    onSignIn: signIn,
    openPlayerProfile: showProfileDialog,
    openActionsMenu: handleOpenMoreOptions,
    removePlayerFromTeam: removeFromTeam,
    tournamentId,
    selectedEventId: selectedEventRef.current,
    showSuspensionWithdrawalWarning: true,
    t,
  });

  const options: TableOptions<ParticipantInterface> = useMemo(
    () => ({
      columns,
      data: players,
      options: {
        autoResetFilters: false,
        autoResetGlobalFilter: false,
      },
      enableRowSelection: true,
      enableMultiRowSelection: true,
      enableColumnFilters: true,
      initialState: {
        pagination: {
          pageIndex: 0,
          pageSize: 25,
        },
        columnFilters: savedFilters ?? [],
      },
      state: {
        columnVisibility,
        rowSelection,
        sorting,
      },
      getRowId: getRowId,
      onSortingChange: setSorting,
      onRowSelectionChange: setRowSelection,
      onColumnVisibilityChange: setColumnVisibility,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
    }),
    [columns, players, columnVisibility, rowSelection, sorting, savedFilters],
  );

  const tableInstance = useReactTable<ParticipantInterface>(options);

  const paginationState = tableInstance.getState().pagination;
  const filters = tableInstance.getState().columnFilters;
  const columnsVisibility = tableInstance.getState().columnVisibility;
  const globalFilter = tableInstance.getState().globalFilter;
  const { setGlobalFilter, setColumnFilters } = tableInstance;
  const selectedFlatRows = tableInstance.getSelectedRowModel().flatRows;
  const { rows } = tableInstance.getRowModel();

  filtersRef.current = useMemo(() => filters, [filters]);
  columnsRef.current = useMemo(() => Object.keys(columnsVisibility), [columnsVisibility]);
  selectedEventRef.current = useMemo(() => {
    const eventFilter = filters.find((filter) => filter.id === 'events');
    return (eventFilter && eventFilter.value !== NO_SELECTION ? eventFilter.value : undefined) as string;
  }, [filters]);

  const gendersPresent = useMemo(() => getGendersPresent(players) || [], [players]);
  const genderMap = useMemo(() => getGenderMap(t), [t]);
  const genderOptions = useMemo(
    () => gendersPresent.map((g) => genderMap[g]).filter((f) => f),
    [genderMap, gendersPresent],
  );

  const saveColumnsSelection = useCallback(
    (columns: Column<ParticipantInterface>[]) => {
      const update = {
        playersColumns: columns.map((column) => column.id),
      };
      if (JSON.stringify(update.playersColumns) === JSON.stringify(savedColumns)) return;

      updateSettings(update);
    },
    [savedColumns, updateSettings],
  );

  const saveFiltersSelection = useCallback(
    (filters: ColumnFiltersState) => {
      if (JSON.stringify(filters) === JSON.stringify(savedFilters)) return;
      const update = {
        playersColumns: columnsRef.current,
        playersFilters: filters,
      };
      updateSettings(update);
    },
    [savedFilters, updateSettings],
  );

  const { isAnyPlayerSuspended, isAnyPlayerRequestedWithdrawn } = useMemo(() => {
    const isAnyPlayerSuspended = tournamentParticipants?.some(
      (participant) => isParticipantSuspended(participant)?.isSuspended,
    );
    const isAnyPlayerRequestedWithdrawn = tournamentParticipants?.some(
      (participant) => isParticipantRequestedWithdrawal(participant, events)?.isRequestedWithdrawal,
    );
    return { isAnyPlayerSuspended, isAnyPlayerRequestedWithdrawn };
  }, [tournamentParticipants, events]);

  useEffect(() => {
    return () => {
      if (filtersRef.current) saveFiltersSelection(filtersRef.current);
    };
  }, [saveFiltersSelection]);

  const tableHasResults = !!rows.length;

  return useMemo(
    () => (
      <>
        <Grid container direction="column" spacing={2}>
          {(isAnyPlayerSuspended || isAnyPlayerRequestedWithdrawn) && (
            <TMXShowOnceAlert
              className={classes.playersWarning}
              dismissable={!(isAnyPlayerSuspended || isAnyPlayerRequestedWithdrawn)}
              storageName="suspendedAndWithdrawnWarning"
            >
              <Grid container alignItems="flex-start" direction="column">
                <Grid container item gap={2} lg={6} md={6} sm={6} xs={6}>
                  <Grid item>
                    <WarningIcon />
                  </Grid>
                  <Grid item lg={6} md={6} sm={6} xs={6}>
                    <BodyLargeBold>{t('suspendedWithdrawnWarning.heading')}</BodyLargeBold>
                    <span>{t('suspendedWithdrawnWarning.listDescription')}</span>
                    <ul>
                      {isAnyPlayerSuspended && <li>{t('suspendedWithdrawnWarning.suspendedText')}</li>}
                      {isAnyPlayerRequestedWithdrawn && <li>{t('suspendedWithdrawnWarning.withdrawnText')}</li>}
                    </ul>
                  </Grid>
                  <Grid item lg={6} md={6} sm={6} xs={6}></Grid>
                </Grid>
              </Grid>
            </TMXShowOnceAlert>
          )}
          {featureEnabled(Feature.RANKINGS_AND_RATINGS) && (
            <Grid container item>
              <RankingsAndRatingsContainer scalesToHide={['Ranking', RatingTypeEnum.PT, RatingTypeEnum.NTRP]} />
            </Grid>
          )}
          <Grid container item alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
            <Grid item>
              <PlayersFilters
                filters={filters}
                genderOptions={genderOptions}
                globalFilter={globalFilter}
                setFilters={setColumnFilters}
                setGlobalFilter={setGlobalFilter}
              />
            </Grid>
            {featureEnabled(Feature.VIRTUAL_CHECK_IN) && (
              <Grid item display="flex" maxWidth={350}>
                <FormControlLabel
                  control={
                    <TMXSwitch
                      checked={!!virtualTournamentCheckIn}
                      name="checkedA"
                      onChange={handleOnToggleTournamentSignIn}
                    />
                  }
                  label={t('tournamentSignInTooltipTitle')}
                  sx={{ marginRight: 0 }}
                />
                <TMXTooltip
                  arrow
                  open={allowTournamentSignInTooltipOpen}
                  placement="top-end"
                  title={
                    <Grid container direction="row" justifyContent={'flex-end'}>
                      <Grid item xs={11}>
                        <Typography className={tooltipClasses.tooltipTitle}>
                          {t('tournamentSignInTooltipTitle')}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <CloseIcon
                          className={tooltipClasses.tooltipCloseIcon}
                          fontSize="small"
                          onClick={handleOnAllowTournamentSignInTooltipClose}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography className={tooltipClasses.tooltipContent}>
                          {t('tournamentSignInTooltipText')}
                        </Typography>
                      </Grid>
                    </Grid>
                  }
                  onClose={handleOnAllowTournamentSignInTooltipClose}
                >
                  <IconButton disableFocusRipple disableRipple onClick={handleOnAllowTournamentSignInTooltipOpen}>
                    <HelpIcon />
                  </IconButton>
                </TMXTooltip>
              </Grid>
            )}
          </Grid>
        </Grid>
        <EnhancedTable
          columnsVisibility={columnsVisibility}
          id="players-table"
          paginationState={paginationState}
          rows={rows}
          saveColumnsSelection={saveColumnsSelection}
          selectedActions={
            <PlayerBulkActions
              selectedFlatRows={selectedFlatRows}
              showTeams={!!teams?.length}
              teams={teams}
              onResetRowSelection={resetRowSelection}
            />
          }
          selectedFlatRows={selectedFlatRows}
          tableInstance={tableInstance}
        />
        {!tableHasResults && <FilteringNoResults />}
      </>
    ),
    [
      isAnyPlayerSuspended,
      isAnyPlayerRequestedWithdrawn,
      classes.playersWarning,
      t,
      filters,
      genderOptions,
      globalFilter,
      setColumnFilters,
      setGlobalFilter,
      virtualTournamentCheckIn,
      handleOnToggleTournamentSignIn,
      allowTournamentSignInTooltipOpen,
      tooltipClasses.tooltipTitle,
      tooltipClasses.tooltipCloseIcon,
      tooltipClasses.tooltipContent,
      handleOnAllowTournamentSignInTooltipClose,
      handleOnAllowTournamentSignInTooltipOpen,
      columnsVisibility,
      paginationState,
      rows,
      saveColumnsSelection,
      selectedFlatRows,
      teams,
      resetRowSelection,
      tableInstance,
      tableHasResults,
    ],
  );
};

export default PlayersTable;
